/* eslint-disable @typescript-eslint/naming-convention */
import type { LabelLink } from '@/utils/linkify/splitLabelUrls';
import type { AnySchema } from 'yup';
import type { RadioGroupOption } from '@/components/form-elements/RadioGroup/types';

/**
 * API Typings
 */

interface PlausiObject {
  bedingung: string;
  value: string[];
}

interface ApiPlausiObject {
  bedingung?: string;
  value: string | string[] | boolean[];
}

interface PlausiArrayObject extends PlausiObject {
  value: ['ELEMENTOFARRAY'];
}

interface Konjunktion {
  konjunktion: {
    plausi: PlausiObject[];
  };
}

interface ApiKonjunktion {
  konjunktion: {
    plausi?: ApiPlausiObject[] | ApiPlausiObject;
    value?: 'ELEMENTOFARRAY';
  };
}

export interface ListBoxElementObject {
  '@content': string;
  '@attributes': {
    value: string;
  };
}

export interface ListBoxElemente {
  element: ListBoxElementObject[];
}

export interface ApiListBoxElemente {
  element: string | string[] | ListBoxElementObject[] | ListBoxElementObject;
}

export type ApiElements = ApiListBoxElemente | ApiKonjunktion | string[];

export type ApiElementsWithoutEmptyArray = ApiListBoxElemente | ApiKonjunktion;

export type Elements = ListBoxElemente | Konjunktion;

/**
 * The generic can be used to type the elements to the correct type.
 * Because we receive a not unified format (element typeof ApiElements) from the actual INNO API request, we need to unify the
 * elements before we start working with them. Therefor the `unifyCompanyQuestions` function must be
 * used. It will return a unified format of the elements (element typeof Elements)
 */
export interface CompanyQuestionApiQuestionObject<T = Elements> {
  questionsDetail: {
    key: string;
    value: {
      gruppe: string;
      gruppennummer: string;
      sortierung: string;
      feldbezeichnung: string;
      pflichtfeld: string;
      beschreibung: string;
      art: string;
      default: never[] | string;
      elemente: T[];
    };
  };
}

export interface CompanyQuestionApiResponse<T = Elements> {
  frageAttribute?: {
    person: {
      ID: string;
      keyValue: CompanyQuestionApiQuestionObject<T>[];
    };
  };
}

/**
 * API TypeGuards
 */

export const isListBoxElementObject = (
  value: unknown,
): value is ListBoxElementObject =>
  Object.hasOwn(value as object, '@content') &&
  Object.hasOwn(value as object, '@attributes');

export const isListBoxElementObjectArray = (
  value: unknown,
): value is ListBoxElementObject[] =>
  Array.isArray(value) &&
  (value.length === 0 || isListBoxElementObject((value as object[])[0]));

export const isListBoxElemente = (value: unknown): value is ListBoxElemente =>
  Object.hasOwn(value as object, 'element') &&
  isListBoxElementObjectArray((value as ListBoxElemente).element);

export const isPlausiElementOfArray = (
  plausi: PlausiObject,
): plausi is PlausiArrayObject =>
  plausi.value.length === 1 && plausi.value[0] === 'ELEMENTOFARRAY';

export const isKonjunktionElement = (value: unknown): value is Konjunktion =>
  Object.hasOwn(value as object, 'konjunktion');

export const isApiKonjunktionElement = (
  value: unknown,
): value is ApiKonjunktion => Object.hasOwn(value as object, 'konjunktion');

export const isEmptyArrayElement = (value: unknown): value is string[] =>
  Array.isArray(value) && value.length === 0;

/**
 * Internal CompanyQuestion Typing
 */

export enum COMPANY_QUESTION_COMPONENT {
  TEXT = 'text',
  NUMBER = 'number',
  SELECT = 'select',
  DATE = 'date',
  CHECKBOX = 'checkbox',
  RADIO = 'radio',
}

export interface CompanyQuestionSelectionOption {
  label: string;
  value: string;
}

export interface CompanyQuestion {
  fieldName: string;
  /** Beschreibung ohne enthaltene URLs */
  label: string;
  /** Aus der Beschreibung gelöste URLs */
  labelLinks?: LabelLink[];
  component: COMPANY_QUESTION_COMPONENT;
  required: boolean;
  default?: string;
  selectOptions: CompanyQuestionSelectionOption[];
  radioOptions: RadioGroupOption[];
  requirements: () => boolean;
  schema: AnySchema;
  position: number;
}

export interface CompanyQuestionGroup {
  label: string;
  groupId: string;
  questions: CompanyQuestion[];
}

export type CompanyQuestionIsRequired = () => boolean;
